import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Badge, Button, Group, Loader, Stack, Text } from "@mantine/core"
import { IconAlertTriangle, IconCheck, IconCircleDashed, IconCircleX } from "@tabler/icons-react"
import type { IReprocessProgress, IStep, TStepStatus } from "../useReprocessProgress"
import classes from "./styles.module.css"

interface IProps {
  /** Live progress from `useReprocessProgress`. */
  progress: IReprocessProgress
  /** Click handler for the close button. */
  onClose: () => void
}

function statusIcon(status: TStepStatus): React.JSX.Element {
  switch (status) {
    case "running":
      return <Loader size="xs" />
    case "completed":
      return <IconCheck size={16} color="var(--mantine-color-success-6)" />
    case "failed":
      return <IconCircleX size={16} color="var(--mantine-color-danger-6)" />
    case "skipped":
      return <IconCircleDashed size={16} color="var(--mantine-color-dimmed)" />
    default:
      return <IconCircleDashed size={16} color="var(--mantine-color-dimmed)" />
  }
}

function statusLabel(status: TStepStatus, i18n: ReturnType<typeof useLingui>["i18n"]): string {
  switch (status) {
    case "running":
      return i18n._(msg`running`)
    case "completed":
      return i18n._(msg`done`)
    case "failed":
      return i18n._(msg`failed`)
    case "skipped":
      return i18n._(msg`skipped`)
    default:
      return i18n._(msg`pending`)
  }
}

function formatDuration(ms: number | undefined): string {
  if (ms === undefined) return ""
  if (ms < 1000) return `${ms} ms`
  return `${(ms / 1000).toFixed(1)} s`
}

function StepRow({ step }: { step: IStep }): React.JSX.Element {
  const { i18n } = useLingui()
  return (
    <Group gap="sm" wrap="nowrap" className={classes.stepRow} data-status={step.status}>
      <span className={classes.stepIcon}>{statusIcon(step.status)}</span>
      <div className={classes.stepBody}>
        <Text size="sm" fw={step.status === "running" ? 600 : 400}>
          {step.name}
        </Text>
        {step.errorMessage && step.status !== "running" && (
          <Text size="xs" c="dimmed" lineClamp={2}>
            {step.errorMessage}
          </Text>
        )}
      </div>
      <div className={classes.stepMeta}>
        {step.durationMs !== undefined && step.status !== "running" && (
          <Text size="xs" c="dimmed">
            {formatDuration(step.durationMs)}
          </Text>
        )}
        <Badge
          size="xs"
          variant="light"
          color={
            step.status === "failed"
              ? "danger"
              : step.status === "completed"
                ? "success"
                : step.status === "running"
                  ? "info"
                  : "gray"
          }
        >
          {statusLabel(step.status, i18n)}
        </Badge>
      </div>
    </Group>
  )
}

export function ReprocessProgress({ progress, onClose }: IProps): React.JSX.Element {
  const { i18n } = useLingui()
  const isDone = progress.done
  const stepsTotal = progress.steps.length
  const stepsDone = progress.steps.filter((s) => s.status === "completed" || s.status === "skipped").length
  const stepsFailed = progress.steps.filter((s) => s.status === "failed").length
  const runningStep = progress.steps.find((s) => s.status === "running")

  return (
    <Stack gap="md">
      <div className={classes.summary}>
        {progress.errored ? (
          <Group gap="xs" align="flex-start">
            <IconAlertTriangle size={18} color="var(--mantine-color-danger-6)" />
            <div>
              <Text fw={600}>
                <Trans>Pipeline error</Trans>
              </Text>
              <Text size="sm" c="dimmed">
                {progress.errorMessage ?? i18n._(msg`A pipeline step failed. The remaining steps continue running.`)}
              </Text>
            </div>
          </Group>
        ) : isDone ? (
          <Group gap="xs">
            <IconCheck size={18} color="var(--mantine-color-success-6)" />
            <Text fw={600}>
              <Trans>Reprocess complete — meeting ready to view.</Trans>
            </Text>
          </Group>
        ) : stepsTotal === 0 ? (
          <Group gap="sm">
            <Loader size="sm" />
            <Text fw={600}>
              <Trans>Preparing pipeline…</Trans>
            </Text>
          </Group>
        ) : (
          <Group gap="sm" wrap="nowrap">
            <Loader size="sm" />
            <div>
              <Text fw={600}>{runningStep ? runningStep.name : i18n._(msg`Working through the pipeline…`)}</Text>
              <Text size="xs" c="dimmed">
                <Trans>
                  {stepsDone} of {stepsTotal} steps complete
                </Trans>
                {stepsFailed > 0 && (
                  <>
                    {" · "}
                    <Trans>{stepsFailed} failed</Trans>
                  </>
                )}
                {progress.progressiveSummaryChunks > 0 && (
                  <>
                    {" · "}
                    <Trans>{progress.progressiveSummaryChunks} transcript chunks</Trans>
                  </>
                )}
              </Text>
            </div>
            {!progress.connected && (
              <Badge color="warning" variant="light" size="sm">
                <Trans>Reconnecting…</Trans>
              </Badge>
            )}
          </Group>
        )}
      </div>

      {progress.steps.length > 0 && (
        <Stack gap={4} className={classes.stepList}>
          {progress.steps.map((step) => (
            <StepRow key={step.alias} step={step} />
          ))}
        </Stack>
      )}

      <Group justify="flex-end" mt="sm">
        <Button onClick={onClose} variant={isDone || progress.errored ? "filled" : "subtle"}>
          {isDone ? <Trans>Close</Trans> : <Trans>Run in background</Trans>}
        </Button>
      </Group>
    </Stack>
  )
}
